import { FC, useState, useEffect, useCallback } from 'react'
import { toast } from 'react-toastify'
import { getCompanyGroupAdmins, getVesselsByCompany } from '../core/_requests'
import { CompanyGroupAdmin, CreateInventoryItemHeadRequest, Vessel } from '../core/_models'
import { useAuth } from '../../auth'
import { useProcurementPermissions } from '../core/permissionAccess'
import { PROCUREMENT_PERMS } from '../core/permissions'
import { libraryUiScope, libraryVesselScopeLocked } from '../core/procurementLibraryUiScope'
import { procurementTenantCgaId } from '../core/procurementTenantContext'
import SearchableSelect from '../../../shared/components/SearchableSelect'

interface HeadModalProps {
  visible: boolean
  onClose: () => void
  onSubmit: (payload: CreateInventoryItemHeadRequest) => Promise<void>
  cgaId?: number
  vesselId?: number
  embedded?: boolean
  stayOpenOnSubmit?: boolean
}

const HeadModal: FC<HeadModalProps> = ({
  visible,
  onClose,
  onSubmit,
  cgaId,
  vesselId,
  embedded = false,
  stayOpenOnSubmit = false,
}) => {
  const { currentUser } = useAuth()
  const { getScope } = useProcurementPermissions()
  const libraryScope = libraryUiScope(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_HEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_HEAD_VIEW,
  )
  const vesselScopeLocked = libraryVesselScopeLocked(
    getScope,
    PROCUREMENT_PERMS.LIBRARY_HEAD_MANAGE,
    PROCUREMENT_PERMS.LIBRARY_HEAD_VIEW,
  )
  const libraryScopeAll = libraryScope === 'ALL'
  const libraryScopeCompany = libraryScope === 'COMPANY'

  const [name, setName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [companies, setCompanies] = useState<CompanyGroupAdmin[]>([])
  const [vessels, setVessels] = useState<Vessel[]>([])
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | ''>('')
  const [selectedVesselId, setSelectedVesselId] = useState<number | ''>('')
  const [isLoadingDropdowns, setIsLoadingDropdowns] = useState(false)

  const effectiveCgaId = libraryScopeCompany ? procurementTenantCgaId(currentUser) : undefined

  const loadCompanies = useCallback(async () => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getCompanyGroupAdmins()
      setCompanies(data)
    } catch (error) {
      console.error('Error loading companies:', error)
      toast.error('Failed to load companies')
      setCompanies([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVessels = useCallback(async (companyUsername: string) => {
    setIsLoadingDropdowns(true)
    try {
      const data = await getVesselsByCompany(companyUsername)
      setVessels(data || [])
    } catch (error) {
      console.error('Error loading vessels:', error)
      toast.error('Failed to load vessels')
      setVessels([])
    } finally {
      setIsLoadingDropdowns(false)
    }
  }, [])

  const loadVesselsForCurrentUser = useCallback(async () => {
    const companyUsername =
      (currentUser as any)?.companyGroupAdmin?.username || (currentUser as any)?.username
    if (!companyUsername) return
    await loadVessels(companyUsername)
  }, [currentUser, loadVessels])

  useEffect(() => {
    if (!visible) return

    if (libraryScopeAll) {
      setSelectedCompanyId(cgaId ?? '')
      setSelectedVesselId(vesselId ?? '')
    } else if (libraryScopeCompany) {
      setSelectedCompanyId('')
      setSelectedVesselId(vesselId ?? '')
    } else {
      setSelectedCompanyId('')
      setSelectedVesselId((currentUser as any)?.vessel?.id ?? vesselId ?? '')
    }
  }, [visible, libraryScopeAll, libraryScopeCompany, cgaId, vesselId, currentUser])

  useEffect(() => {
    if (!visible) return

    if (libraryScopeAll) {
      loadCompanies()
    } else if (libraryScopeCompany) {
      loadVesselsForCurrentUser()
    }
  }, [visible, libraryScopeAll, libraryScopeCompany, loadCompanies, loadVesselsForCurrentUser])

  useEffect(() => {
    if (!visible || !libraryScopeAll) return
    if (!selectedCompanyId) {
      setVessels([])
      setSelectedVesselId('')
      return
    }

    const selectedCompany = companies.find((c) => c.id === Number(selectedCompanyId))
    if (selectedCompany?.username) {
      loadVessels(selectedCompany.username)
    }
  }, [visible, libraryScopeAll, selectedCompanyId, companies, loadVessels])

  const resetState = () => {
    setName('')
    setCompanies([])
    setVessels([])
    setSelectedCompanyId('')
    setSelectedVesselId('')
    setIsLoading(false)
  }

  const handleClose = () => {
    resetState()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      toast.error('Please enter a head name')
      return
    }

    let scopedCgaId: number | null = null
    let scopedVesselId: number | null = null

    if (libraryScopeAll) {
      if (!selectedCompanyId) {
        toast.error('Please select a company')
        return
      }
      scopedCgaId = Number(selectedCompanyId)
      scopedVesselId = selectedVesselId ? Number(selectedVesselId) : null
    } else if (libraryScopeCompany) {
      const fallbackCgaId = effectiveCgaId ?? cgaId ?? null
      if (!fallbackCgaId) {
        toast.error('Unable to resolve company scope for this user')
        return
      }
      scopedCgaId = Number(fallbackCgaId)
      scopedVesselId = selectedVesselId ? Number(selectedVesselId) : null
    } else {
      const userVesselId = (currentUser as any)?.vessel?.id ?? vesselId ?? null
      scopedVesselId = userVesselId ? Number(userVesselId) : null
      scopedCgaId = cgaId ?? null
    }

    const payload: CreateInventoryItemHeadRequest = {
      name: name.trim(),
      active: true,
      inventoryItemCategoryId: null,
      vesselId: scopedVesselId,
      cgaid: scopedCgaId ? { id: scopedCgaId } : null,
    }

    if (!payload.cgaid && !payload.vesselId) {
      toast.error('Please select a valid company or vessel scope')
      return
    }
    if (vesselScopeLocked && !payload.vesselId) {
      toast.error('Vessel is required')
      return
    }

    setIsLoading(true)
    try {
      await onSubmit(payload)
      toast.success('Head created successfully!')
      if (stayOpenOnSubmit) {
        resetState()
      } else {
        handleClose()
      }
    } catch (error: any) {
      console.error('Error creating head:', error)
      toast.error('Failed to create head')
    } finally {
      setIsLoading(false)
    }
  }

  if (!visible) return null

  const modalContent = (
    <div
      className='modal-content bg-white'
      style={{
        height: embedded ? '100%' : undefined,
        borderRadius: embedded ? '12px' : undefined,
        boxShadow: embedded ? '0 12px 40px rgba(15,23,42,0.06)' : undefined,
        border: embedded ? '1px solid #E9EEF5' : undefined,
      }}
    >
      <div className='modal-header border-bottom'>
        <h3 className='modal-title fw-bold text-dark'>Add New Head</h3>
        <button type='button' className='btn-close' onClick={handleClose}></button>
      </div>

      <form onSubmit={handleSubmit} style={{height: embedded ? 'calc(100% - 60px)' : undefined, display: 'flex', flexDirection: 'column'}}>
        <div className='modal-body' style={{overflowY: embedded ? 'auto' : undefined, flex: embedded ? '1 1 auto' : undefined}}>
          {libraryScopeAll && (
            <div className='row g-3 mb-4'>
              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7 required'>Company</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedCompanyId}
                  onChange={(e) => {
                    const nextCompanyId = e.target.value ? Number(e.target.value) : ''
                    setSelectedCompanyId(nextCompanyId)
                    setSelectedVesselId('')
                  }}
                  disabled={isLoadingDropdowns}
                  required
                >
                  <option value=''>Select a company</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>

              <div className='col-md-6'>
                <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
                <SearchableSelect
                  className='form-select'
                  value={selectedVesselId}
                  onChange={(e) => {
                    const nextVesselId = e.target.value ? Number(e.target.value) : ''
                    setSelectedVesselId(nextVesselId)
                  }}
                  disabled={isLoadingDropdowns || !selectedCompanyId}
                >
                  <option value=''>Company level (no vessel)</option>
                  {vessels.map((vessel) => (
                    <option key={vessel.id} value={vessel.id}>
                      {vessel.fleet_name}
                    </option>
                  ))}
                </SearchableSelect>
              </div>
            </div>
          )}

          {libraryScopeCompany && (
            <div className='mb-4'>
              <label className='form-label fw-semibold text-muted fs-7'>Vessel (optional)</label>
              <SearchableSelect
                className='form-select'
                value={selectedVesselId}
                onChange={(e) => {
                  const nextVesselId = e.target.value ? Number(e.target.value) : ''
                  setSelectedVesselId(nextVesselId)
                }}
                disabled={isLoadingDropdowns}
              >
                <option value=''>Company level (no vessel)</option>
                {vessels.map((vessel) => (
                  <option key={vessel.id} value={vessel.id}>
                    {vessel.fleet_name}
                  </option>
                ))}
              </SearchableSelect>
            </div>
          )}

          <div className='mb-4'>
            <label className='form-label fw-semibold text-muted fs-7 required'>Head Name</label>
            <input
              type='text'
              className='form-control'
              placeholder='Enter head name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
        </div>

        <div className='modal-footer border-top' style={{flexShrink: 0}}>
          <button
            type='button'
            className='btn btn-light'
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            type='submit'
            className='btn btn_primary'
            disabled={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Head'}
          </button>
        </div>
      </form>
    </div>
  )

  if (embedded) {
    return (
      <div
        className='h-100 d-flex flex-column'
        style={{background: '#F8FAFC', padding: '12px', overflow: 'hidden'}}
      >
        {modalContent}
      </div>
    )
  }

  return (
    <div
      className="modal fade show d-flex align-items-center justify-content-center"
      tabIndex={-1}
      style={{
        backgroundColor: 'rgba(0,0,0,0.5)',
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 1050,
      }}
    >
      <div className='modal-dialog modal-dialog-centered' role='document'>
        {modalContent}
      </div>
    </div>
  )
}

export default HeadModal
